import T from 'Components/Helpers/translate';

// Maps a translation key to the raw entity_type / sub_type tokens it represents.
// The backend LIKEs both columns, so a value can be either kind of token — and the
// UI label often differs from the stored token (e.g. "Completed" is sub_type "Closed",
// "Rejected" is sub_type "Declined"), which is exactly why this can't be derived.
// Keys are the labels the timeline actually renders (nouns AND action verbs), so a
// user searching the word they see ("commented", "signed", "rejected") gets a match.
const LANG_KEY_TO_TOKENS: Readonly<Record<string, string[]>> = {
    // Entity nouns
    'default.activity': ['Activity'],
    'default.task': ['Activity'],
    'default.todo': ['Activity'],
    'default.phonecall': ['Activity'],
    'default.appointment': ['Appointment'],
    'default.order': ['Order', 'Agreement'],
    'default.agreement': ['Agreement'],
    'default.opportunity': ['Opportunity'],
    'default.opportunities': ['Opportunity'],
    'default.mail': ['Mail'],
    'default.email': ['Mail'],
    'default.comment': ['Comment'],
    'default.client': ['Client'],
    'default.contact': ['Contact'],
    'default.lead': ['Lead'],
    'default.leads': ['Lead'],
    'default.visit': ['Visit'],
    'default.visits': ['Visit'],
    'default.ticket': ['Ticket'],
    'default.tickets': ['Ticket'],
    'default.socialEvent': ['SocialEvent'],
    'default.socialEvents': ['SocialEvent'],
    'default.signature': ['Esign'],
    'default.notes': ['Note'],
    // Action verbs / statuses the timeline renders
    'default.created': ['Created'],
    'default.completed': ['Completed', 'Closed'],
    'default.commented': ['Comment'],
    'default.won': ['Won'],
    'default.lost': ['Lost'],
    'default.clicked': ['Click'],
    'mail.opened': ['open'],
    'mail.bounced': ['bounce'],
    'mail.unsubscribed': ['unsub'],
    'esign.didSend': ['Sent'],
    'esign.didSign': ['Signed'],
    'esign.rejected': ['Declined'],
    'esign.viewed': ['Viewed'],
    'socialEvent.status.attended': ['Attended'],
    'socialEvent.status.attending': ['Attending'],
    'socialEvent.status.declined': ['Declined'],
    'socialEvent.status.invited': ['Invited']
};

let cachedReverseMap: Map<string, string[]> | null = null;

function buildReverseMap(): Map<string, string[]> {
    const map = new Map<string, string[]>();
    const t = T;
    Object.entries(LANG_KEY_TO_TOKENS).forEach(([key, tokens]) => {
        const translated = t(key);
        if (typeof translated !== 'string' || !translated || translated === key) {
            return;
        }
        const normalized = translated.toLowerCase().trim();
        if (normalized.length < 3) {
            return;
        }
        const existing = map.get(normalized) ?? [];
        tokens.forEach(token => {
            if (!existing.includes(token)) {
                existing.push(token);
            }
        });
        map.set(normalized, existing);
    });
    return map;
}

export function expandHistoryLogSearch(term: string): string | string[] {
    if (typeof term !== 'string' || term.length < 3) {
        return term;
    }
    if (!cachedReverseMap) {
        cachedReverseMap = buildReverseMap();
    }
    const synonyms = cachedReverseMap.get(term.toLowerCase().trim());
    if (!synonyms || !synonyms.length) {
        return term;
    }
    const expanded = [term];
    synonyms.forEach(token => {
        if (!expanded.some(existing => existing.toLowerCase() === token.toLowerCase())) {
            expanded.push(token);
        }
    });
    return expanded.length === 1 ? expanded[0] : expanded;
}
